import { ITEM_INITIAL_VALUE } from "../../FormularioPropuestaV2";
import {
  INTRO_TAXTIC,
  CLAUSULA_CONFIDENCIALIDAD,
} from "../../FormularioPropuestaV2/constants/textosFijos";
import { generarUuid } from "./generarUuid";

/**
 * Normaliza una fecha del backend al formato ISO `YYYY-MM-DD` que esperan
 * date-fns parseISO y el DatePicker de @material-ui/lab.
 *
 * Casos soportados:
 *  - "YYYY/MM/DD"  → "YYYY-MM-DD"
 *  - "YYYY-MM-DD"  → sin cambios
 *  - "YYYY/MM"     → "YYYY-MM-01"  (formato legacy del backend)
 *  - "" / null     → ""
 */
const normalizarFechaISO = (fecha) => {
  if (!fecha) return "";
  const limpia = String(fecha).replace(/\//g, "-");
  if (/^\d{4}-\d{2}$/.test(limpia)) return `${limpia}-01`;
  return limpia;
};

/**
 * Convierte la respuesta de GET /propuestas/v2/:id en initialValues de Formik
 * con el mismo shape que usa FormularioPropuestaV2.
 *
 * El resolver indexPropuestaV2 devuelve
 *   - equipo: [{ rolDisplay, fotoUrl, descripcionExperiencia, ... }]
 *   - items:  [{ tipoItem, refCliente, faseTitulo, valorConDescuento, ..., cliente, empleado }]
 *   - solicitante: { id, nombre, correo, telefono, rut }
 *
 * Cada item y cada miembro recibe `__key` uuid client-side — los ids de BD se
 * regeneran en cada PATCH (delete+insert atómico), por lo que no son keys estables
 * para React.
 */
export const propuestaV2ToFormValues = (propuesta) => {
  const items = (propuesta.items ?? []).map((it) => ({
    ...ITEM_INITIAL_VALUE,
    __key: generarUuid(),
    tipoItem: it.tipoItem ?? "PLAN",
    refCliente: it.refCliente ?? "",
    nombreCliente: it.cliente?.nombre ?? "",
    refEmpleado: it.refEmpleado ?? "",
    nombreServicio: it.nombreServicio ?? "",
    tipoMoneda: it.tipoMoneda ?? "CLP",
    valorOriginal: Number(it.valorOriginal ?? 0),
    descuento: Number(it.descuento ?? 0),
    valorConDescuento: Number(it.valorConDescuento ?? 0),
    observaciones: it.observaciones ?? "",
    // PLAN
    tipoFrecuencia: it.tipoFrecuencia ?? "MES",
    fechaSiguienteCiclo: normalizarFechaISO(it.fechaSiguienteCiclo),
    mesCargaTareas: it.mesCargaTareas ?? "",
    mesFacturacion: it.mesFacturacion ?? "",
    seFacturaGestor: it.seFacturaGestor === 1 || it.seFacturaGestor === true,
    fechaTerminoDesc: normalizarFechaISO(it.fechaTerminoDesc),
    // TAREA
    fechaLimite: normalizarFechaISO(it.fechaLimite),
    vendidoTaxtic: it.vendidoTaxtic === 1 || it.vendidoTaxtic === true,
    // catálogo + narrativa
    refTipoPlan: it.refTipoPlan ?? "",
    refTipoTarea: it.refTipoTarea ?? "",
    faseTitulo: it.faseTitulo ?? "",
    faseObjetivo: it.faseObjetivo ?? "",
    faseActividades: it.faseActividades ?? "",
    faseEntregable: it.faseEntregable ?? "",
    faseDuracion: it.faseDuracion ?? "",
  }));

  const equipo = (propuesta.equipo ?? []).map((m) => ({
    ...m,
    __key: generarUuid(),
  }));

  return {
    id: propuesta.id ?? null,
    titulo: propuesta.titulo ?? "",
    descripcion: propuesta.descripcion ?? "",
    fecha: normalizarFechaISO(propuesta.fecha),
    solicitanteId: propuesta.solicitante?.id ?? null,
    solicitanteNuevo: null,
    solicitanteData: propuesta.solicitante ?? null,
    sucursal: propuesta.sucursal ?? "",
    introduccion: propuesta.introduccion ?? INTRO_TAXTIC,
    resumenEjecutivo: propuesta.resumenEjecutivo ?? "",
    antecedentes: propuesta.antecedentes ?? "",
    alcance: propuesta.alcance ?? "",
    items,
    vendedor: propuesta.vendedor ?? "",
    vendedorCorreo: propuesta.vendedorCorreo ?? "",
    vendedorTelefono: propuesta.vendedorTelefono ?? "",
    supervisor: propuesta.supervisor ?? "",
    equipo,
    condicionesPago: propuesta.condicionesPago ?? "",
    duracionEstimada: propuesta.duracionEstimada ?? "",
    proximosPasos: propuesta.proximosPasos ?? "",
    clausulaConfidencialidad:
      propuesta.clausulaConfidencialidad ?? CLAUSULA_CONFIDENCIALIDAD,
    generadoConIA: Boolean(propuesta.generadoConIA),
    promptOriginal: propuesta.promptOriginal ?? "",
    origenMigracion: null,
  };
};
